import React from 'react';
import portalArt from '../../assets/img/portal-o.webp';
import { shareTemplate } from '../../constants/shareTemplates';
import useImageSize from './useImageSize';

/**
 * The in-app 1080×1920 story and 1080×1080 feed card, in Ring, Streak or Marquee
 * (design_handoff_share_card_templates, V2Story). The Portal story, the Party Starter story and the
 * Share Sheet's story image are all this card. It is captured with html-to-image, so `image` should
 * already be a same-origin or data URL. A poster is never cropped.
 */
const PARTY = '#FD199F,#FF33C6,#B5B1FF,#127BFD,#22ECFB,#FAE1B4,#FCAA6D';
const LOOP = `linear-gradient(90deg,${PARTY},#FD199F)`;
const KIT = {
  story: { bottom: 96, gap: 28, gapL: 44, init: 96, initFont: 44, top: 28, meta: 30, number: 64, line: 26, bar: 18, cta: 116, ctaFont: 40, dom: 26, qr: 260, pill: 52, pillPad: 24, pillFont: 22, cap: 48, pad: 80, capTop: 104 },
  feed: { bottom: 60, gap: 18, gapL: 26, init: 72, initFont: 34, top: 22, meta: 23, number: 48, line: 20, bar: 12, cta: 88, ctaFont: 30, dom: 20, qr: 180, pill: 42, pillPad: 18, pillFont: 18, cap: 36, pad: 64, capTop: 64 },
};
const LOCKUP_PAD = 208 / 3416;

export const contain = (iw, ih, bx, by, bw, bh) => {
  const s = Math.min(bw / iw, bh / ih);
  const [w, h] = [iw * s, ih * s];
  return { left: bx + (bw - w) / 2, top: by + (bh - h) / 2, width: w, height: h };
};

const abs = (r) => ({ position: 'absolute', ...r });

function Bracket({ H, feed }) {
  const [m, len] = feed ? [24, 32] : [32, 44];
  const t = 3;
  const [x0, y0, x1, y1] = [m, m, 1080 - m, H - m];
  const bars = [
    [x0, y0, len, t], [x0, y0, t, len], [x1 - len, y0, len, t], [x1 - t, y0, t, len],
    [x0, y1 - t, len, t], [x0, y1 - len, t, len], [x1 - len, y1 - t, len, t], [x1 - t, y1 - len, t, len],
  ];
  return bars.map(([left, top, width, height]) => (
    <div key={`${left}-${top}-${width}`} style={abs({ left, top, width, height, background: 'rgba(255,255,255,.32)' })} />
  ));
}

export default function StoryCard({
  template = 'ring', format = 'story', image = null, fallback = 'swirl', accent = '#FF33C6',
  status = '', statusBg = '#fff', init = '', topline = '', keyword = '', words = '', serif = false,
  meta = '', pct = null, frac = '', number = '', line = '', qr = null, code = '', cta = '', ctaKind = 'ring',
  showLogo = true, showWords = true, refEl = null, style = null,
}) {
  const t = shareTemplate(template);
  const feed = format === 'feed';
  const H = feed ? 1080 : 1920;
  const k = feed ? KIT.feed : KIT.story;
  const measured = useImageSize(image);
  const { w: iw, h: ih } = measured || { w: 720, h: 1280 };
  const hasImage = !!image;
  const art = fallback === 'door' ? '/logo512.png' : portalArt;
  const tall = !feed && (qr || (pct && line));
  const dim = t === 'ring' ? 0.55 : t === 'streak' ? 0.3 : 0.4;
  const mask = { WebkitMaskImage: 'radial-gradient(circle,#000 52%,transparent 70%)', maskImage: 'radial-gradient(circle,#000 52%,transparent 70%)' };

  let body = null;
  if (t === 'ring') {
    const box = feed ? contain(iw, ih, 560, 150, 440, 420) : tall ? contain(iw, ih, 200, 220, 680, 560) : contain(iw, ih, 150, 240, 780, 820);
    const target = hasImage ? box : (feed ? { left: 600, top: 150, width: 400, height: 400 } : { left: 200, top: 300, width: 680, height: 680 });
    const s = Math.max(target.width, target.height) * (hasImage ? 1.6 : 1.5);
    body = (
      <>
        <img src={portalArt} alt="" style={abs({ left: target.left + target.width / 2 - s / 2, top: target.top + target.height / 2 - s / 2, width: s, height: s })} />
        {hasImage && (
          <div style={abs({ left: box.left - 8, top: box.top - 8, width: box.width + 16, height: box.height + 16, boxSizing: 'border-box', border: '8px solid transparent', background: `linear-gradient(#000,#000) padding-box, linear-gradient(135deg,${PARTY}) border-box` })}>
            <img src={image} alt="" style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        )}
      </>
    );
  } else if (t === 'streak') {
    const panelTop = feed ? 560 : tall ? 800 : 1100;
    const size = feed ? 460 : 840;
    body = (
      <>
        {hasImage
          ? <img src={image} alt="" style={{ ...abs(contain(iw, ih, 60, feed ? 150 : 210, 960, panelTop - (feed ? 190 : 270))), outline: '3px solid #fff' }} />
          : <img src={art} alt="" style={{ ...abs({ left: 540 - size / 2, top: (panelTop + 160) / 2 - size / 2, width: size, height: size }), ...mask }} />}
        <div style={abs({ left: 0, top: panelTop, width: 1080, bottom: 0, background: '#000' })} />
        <div style={abs({ left: 0, top: panelTop - 12, width: 1080, height: 12, background: `linear-gradient(90deg,${PARTY})` })} />
      </>
    );
  } else {
    const size = feed ? 600 : 1000;
    body = (
      <>
        {hasImage
          ? <img src={image} alt="" style={abs(contain(iw, ih, 0, 0, 1080, feed ? 1080 : tall ? 1000 : 1500))} />
          : <img src={art} alt="" style={{ ...abs({ left: 540 - size / 2, top: (feed ? 380 : 620) - size / 2, width: size, height: size }), ...mask }} />}
        <div style={abs({ inset: 0, background: 'linear-gradient(0deg, rgba(0,0,0,.95) 0%, rgba(0,0,0,.78) 38%, rgba(0,0,0,0) 66%), linear-gradient(180deg, rgba(0,0,0,.65) 0%, rgba(0,0,0,0) 16%)' })} />
      </>
    );
  }

  const len = words.length;
  const wordsSize = feed ? (len <= 30 ? 76 : len <= 60 ? 60 : len <= 100 ? 48 : 40) : (len <= 30 ? 104 : len <= 60 ? 84 : len <= 100 ? 66 : 54);
  const clamp = feed ? 3 : 4;
  const clampStyle = { display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: clamp, overflow: 'hidden', textWrap: 'balance' };
  const lockW = (6.25 * k.cap) / (1 - 2 * LOCKUP_PAD);
  const ctaBg = ctaKind === 'solid' ? LOOP : ctaKind === 'white' ? '#fff' : `linear-gradient(#000,#000) padding-box, ${LOOP} border-box`;
  const ctaInk = ctaKind === 'ring' ? '#fff' : '#000';

  return (
    <div
      ref={refEl}
      data-testid="story-card"
      data-template={t}
      style={{ position: 'relative', width: 1080, height: H, background: '#000', overflow: 'hidden', fontFamily: "'Courier Prime', ui-monospace, monospace", color: '#fff', ...style }}
    >
      {hasImage && (
        <>
          <img src={image} alt="" style={{ ...abs({ left: '-10%', top: '-10%', width: '120%', height: '120%' }), objectFit: 'cover', filter: 'blur(60px) saturate(1.4)' }} />
          <div style={abs({ inset: 0, background: `rgba(0,0,0,${dim})` })} />
        </>
      )}
      {body}
      <div style={abs({ inset: 0, pointerEvents: 'none', background: 'repeating-linear-gradient(to bottom, rgba(255,255,255,.045) 0px, rgba(255,255,255,.045) 2px, transparent 2px, transparent 6px)' })} />

      {showLogo && <img src="/brand/portal-lockup-white.png" alt="Portal" style={abs({ left: k.pad - lockW * LOCKUP_PAD, top: k.capTop - lockW * LOCKUP_PAD, width: lockW })} />}
      {status && (
        <div style={abs({ right: k.pad, top: k.capTop - 6, height: k.pill, padding: `0 ${k.pillPad}px`, borderRadius: 999, background: statusBg, color: '#000', display: 'flex', alignItems: 'center', fontSize: k.pillFont, fontWeight: 700, letterSpacing: '.16em' })}>{status}</div>
      )}

      <div style={abs({ left: k.pad, right: k.pad, bottom: k.bottom, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' })}>
        <div style={{ display: 'flex', alignItems: 'center', gap: k.gap, marginBottom: k.gap, maxWidth: '100%' }}>
          {init && <div style={{ width: k.init, height: k.init, flex: 'none', boxSizing: 'border-box', border: '4px solid #fff', background: '#161616', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: k.initFont, fontWeight: 700 }}>{init}</div>}
          <div style={{ minWidth: 0, fontSize: k.top, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', lineHeight: 1.3 }}>[ {topline}<span style={{ color: accent }}>{keyword}</span> ]</div>
        </div>
        {showWords && (serif
          ? <div style={{ fontFamily: "'Times New Roman',Times,serif", fontStyle: 'italic', fontSize: wordsSize, lineHeight: 1.12, ...clampStyle }}>“{words}”</div>
          : <div style={{ fontSize: wordsSize, lineHeight: 1.06, fontWeight: 700, ...clampStyle }}>{words}</div>)}
        {meta && <div style={{ marginTop: k.gap, fontSize: k.meta, fontWeight: 700, letterSpacing: '.18em', color: 'rgba(255,255,255,.85)', textTransform: 'uppercase', maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{meta}</div>}
        {pct && (
          <div style={{ width: '100%', marginTop: k.gapL, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: k.meta, fontWeight: 700, letterSpacing: '.14em' }}><span style={{ color: accent }}>{number}</span><span style={{ color: 'rgba(255,255,255,.72)' }}>{frac}</span></div>
            <div style={{ height: k.bar, borderRadius: 999, background: 'rgba(255,255,255,.18)', overflow: 'hidden' }}><div style={{ height: '100%', width: pct, borderRadius: 999, background: `linear-gradient(90deg,${PARTY})` }} /></div>
          </div>
        )}
        {!pct && number && <div style={{ marginTop: k.gap, fontSize: k.number, fontWeight: 700, lineHeight: 1.1, color: accent }}>{number}</div>}
        {line && <div style={{ marginTop: k.gap, fontSize: k.line, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', lineHeight: 1.45, color: 'rgba(255,255,255,.8)' }}>{line}</div>}
        {(qr || code) && (
          <div style={{ marginTop: k.gapL, display: 'flex', alignItems: 'center', gap: 36 }}>
            {qr && <div style={{ width: k.qr, height: k.qr, background: '#fff', borderRadius: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><img src={qr} alt="" style={{ width: '86%', height: '86%' }} /></div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {qr && <div style={{ fontSize: k.meta, fontWeight: 700, letterSpacing: '.18em', color: 'rgba(255,255,255,.7)' }}>SCAN TO JOIN</div>}
              {code && <div style={{ fontSize: k.number, fontWeight: 700, letterSpacing: '.06em' }}>{code}</div>}
            </div>
          </div>
        )}
        <div style={{ alignSelf: 'stretch', marginTop: k.gapL, height: k.cta, border: '4px solid transparent', borderRadius: 999, boxSizing: 'border-box', background: ctaBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: k.ctaFont, fontWeight: 700, letterSpacing: '.18em', color: ctaInk }}>{cta}</div>
        <div style={{ alignSelf: 'center', marginTop: k.gap, fontSize: k.dom, fontWeight: 700, letterSpacing: '.3em', color: 'rgba(255,255,255,.62)' }}>ONLY ON HEYPORTAL.APP</div>
      </div>

      {t === 'marquee' && <div style={abs({ left: 0, bottom: 0, width: 1080, height: 10, background: LOOP })} />}
      {t !== 'marquee' && <Bracket H={H} feed={feed} />}
    </div>
  );
}
